import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../api/endpoints.js';
import { Page } from '../../components/AppShell.jsx';
import { Button, Card, DefinitionList, ErrorState, Field, Loading, Select, Textarea } from '../../components/ui.jsx';
import { toast } from '../../lib/toast.js';
import { date, phoneWithCountryCode } from '../../lib/format.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { PERMISSIONS } from '../../lib/permissions.js';
import { CATEGORY_LABELS, KIND_LABELS, StatusBadge, STATUS_LABELS } from './supportShared.jsx';

/**
 * One query: the whole conversation, who asked, and the reply box.
 *
 * Replying can also move the query on in the same step ("reply and resolve").
 * Left alone, a reply to an open query makes it "in progress". The person gets
 * a notification and reads it as "Grihvaas support", not your name.
 */
export default function SupportDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const canRespond = can(PERMISSIONS.SUPPORT_RESPOND);

  const [body, setBody] = useState('');
  const [then, setThen] = useState('');

  const key = ['admin-support-query', id];
  const query = useQuery({ queryKey: key, queryFn: () => api.support.detail(id) });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: key });
    queryClient.invalidateQueries({ queryKey: ['admin-support'] });
    queryClient.invalidateQueries({ queryKey: ['admin-support-summary'] });
  };

  const reply = useMutation({
    mutationFn: () => api.support.reply(id, { body, ...(then ? { status: then } : {}) }),
    onSuccess: () => {
      toast.success('Reply sent.');
      setBody('');
      setThen('');
      refresh();
    },
    onError: (error) => toast.error(error.message || 'The reply could not be sent.'),
  });
  const setStatus = useMutation({
    mutationFn: (status) => api.support.setStatus(id, status),
    onSuccess: () => {
      toast.success('Status updated.');
      refresh();
    },
    onError: (error) => toast.error(error.message || 'The status could not be changed.'),
  });

  if (query.isLoading) return <Loading label="Opening the query" />;
  if (query.isError) return <ErrorState error={query.error} onRetry={query.refetch} />;

  const item = query.data;
  const closed = item.status === 'closed';

  return (
    <Page
      title={item.subject}
      subtitle={`#${item.ref} · ${CATEGORY_LABELS[item.category] || item.category} · raised ${date(item.createdAt, { withTime: true })}`}
      actions={<Button variant="ghost" onClick={() => navigate('/support')}>Back to support</Button>}
    >
      <Card title="Who asked">
        <DefinitionList
          items={[
            { label: 'Status', value: <StatusBadge status={item.status} /> },
            { label: 'Name', value: item.user?.name },
            { label: 'Phone', value: item.user?.phone && <span className="mono">{phoneWithCountryCode(item.user.phone)}</span> },
            { label: 'App', value: KIND_LABELS[item.user?.kind] },
          ]}
        />
      </Card>

      <Card title="Conversation">
        <ol style={{ listStyle: 'none', display: 'grid', gap: 12 }}>
          {item.messages.map((message) => {
            const fromUser = message.from === 'user';
            return (
              <li
                key={message.id}
                style={{
                  justifySelf: fromUser ? 'start' : 'end',
                  maxWidth: 'min(620px, 100%)',
                  padding: '10px 14px',
                  borderRadius: 12,
                  background: fromUser ? 'var(--surface-low)' : 'var(--primary-fixed)',
                  color: 'var(--primary)',
                }}
              >
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>
                  {fromUser ? item.user?.name || 'The user' : `Support${message.adminName ? ` · ${message.adminName}` : ''}`}
                  {' · '}
                  {date(message.createdAt, { withTime: true })}
                </div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{message.body}</div>
              </li>
            );
          })}
        </ol>
      </Card>

      {canRespond ? (
        <Card title={closed ? 'This query is closed' : 'Reply'}>
          {closed ? (
            <p className="muted">The person can no longer add to it. Reopen it to answer.</p>
          ) : (
            <>
              <Field label="Your reply" hint="The person sees this as “Grihvaas support”.">
                <Textarea rows={5} value={body} maxLength={4000} onChange={(event) => setBody(event.target.value)} />
              </Field>
              <div className="toolbar" style={{ marginTop: 12 }}>
                <Select value={then} onChange={(event) => setThen(event.target.value)} aria-label="After sending">
                  <option value="">After sending: keep as is</option>
                  <option value="in_progress">After sending: mark in progress</option>
                  <option value="resolved">After sending: mark resolved</option>
                  <option value="closed">After sending: close it</option>
                </Select>
                <Button variant="primary" disabled={!body.trim() || reply.isPending} onClick={() => reply.mutate()}>
                  {reply.isPending ? 'Sending…' : 'Send reply'}
                </Button>
              </div>
            </>
          )}
          <div className="toolbar" style={{ marginTop: 16 }}>
            <Select value={item.status} onChange={(event) => setStatus.mutate(event.target.value)} aria-label="Change status" disabled={setStatus.isPending}>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>Status: {label}</option>
              ))}
            </Select>
          </div>
        </Card>
      ) : null}
    </Page>
  );
}
